import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Calendar, AlertTriangle, ArrowRight, ChevronDown, ChevronRight as ChevronRightIcon, Wallet } from 'lucide-react';

import { useApi } from '../lib/api.js';
import { useCurrentUser } from '../hooks/useCurrentUser.js';
import { StatusPill } from '../components/ui/Feedback.jsx';
import { computeDisplayStatus } from '../lib/computeStatus.js';

// =============================================================================
// Dashboard
// =============================================================================
// Lives at "/" — the operational at-a-glance view. Four stat tiles up top,
// today's bookings feed in the middle, attention items at the bottom.
//
// Data sources are existing endpoints — no new backend needed:
//   - GET /bookings?date=today          → today's bookings list + counts
//   - GET /drivers?status=active         → active chauffeur count (compliance-filtered)
//   - GET /vehicles?status=active        → active vehicle count (compliance-filtered)
//
// "Truly assignable" counts use the is_compliant flag from the compliance
// enrichment we shipped in 7d.5.7 — so a chauffeur who's "active" but has
// expired docs doesn't get counted as available.
// =============================================================================

export function DashboardPage() {
    const api = useApi();
    const navigate = useNavigate();
    const { user } = useCurrentUser();

    const hour = new Date().getHours();
    const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
    const firstName = user?.full_name?.split(' ')[0] || 'there';

// Date toggle — Today (default) or Tomorrow. Operationally, allocations
    // are typically done a day ahead, so dispatchers flip to Tomorrow during
    // their afternoon planning. Feed + stat tiles flip together; the
    // attention queue (compliance issues) stays date-agnostic since those
    // problems exist regardless of which day's bookings you're looking at.
    const [dateView, setDateView] = useState('today');

    const selectedDate = useMemo(() => {
        const d = new Date();
        if (dateView === 'tomorrow') d.setDate(d.getDate() + 1);
        return d.toISOString().slice(0, 10);
    }, [dateView]);

    const datePreset = dateView === 'tomorrow' ? 'tomorrow' : 'today';
    const dateLabel = dateView === 'tomorrow' ? 'Tomorrow' : 'Today';
    const dateLabelLower = dateView === 'tomorrow' ? 'tomorrow' : 'today';

    // Bookings for the selected date — used for the count and the live feed
    const bookingsQuery = useQuery({
        queryKey: ['bookings', { date_from: selectedDate, date_to: selectedDate }],
        queryFn: () => api.get('/bookings', { query: { date_from: selectedDate, date_to: selectedDate } }),
        refetchInterval: 60_000,
    });

    // All active chauffeurs — we filter for is_compliant client-side
    const chauffeursQuery = useQuery({
        queryKey: ['drivers', { status: 'active' }],
        queryFn: () => api.get('/drivers', { query: { status: 'active' } }),
    });

    // All active vehicles — same compliance filter applied
    const vehiclesQuery = useQuery({
        queryKey: ['vehicles', { status: 'active' }],
        queryFn: () => api.get('/vehicles', { query: { status: 'active' } }),
    });

    // Pending portal uploads — owner-only review queue. Refetches every
    // minute alongside bookings so the dashboard stays current. Errors
    // here are silently ignored (network blips shouldn't blank the page);
    // the count just renders as 0 in that case.
    const pendingUploadsQuery = useQuery({
        queryKey: ['compliance', 'pending'],
        queryFn: () => api.get('/compliance/pending'),
        refetchInterval: 60_000,
        // Only owners can hit this endpoint — non-owners would 403.
        enabled: !!user && user.role === 'owner',
    });

    const pendingInvoicesQuery = useQuery({
    queryKey: ['bookings', 'pending-invoices-count'],
    queryFn: () => api.get('/bookings/pending-invoices/count'),
    enabled: user?.role === 'owner',
    staleTime: 60_000,
});

    const unpaidStatementsQuery = useQuery({
        queryKey: ['statements', 'unpaid'],
        queryFn: () => api.get('/statements/unpaid'),
        enabled: user?.role === 'owner',
        staleTime: 60_000,
    });

    const pendingPoolQuery = useQuery({
        queryKey: ['statements', 'pool-pending'],
        queryFn: () => api.get('/statements/pool/pending-by-chauffeur'),
        enabled: user?.role === 'owner',
        staleTime: 60_000,
    });

        // Bookings waiting for payment (any date). One row per itinerary.
    const awaitingPaymentQuery = useQuery({
        queryKey: ['bookings', { status: 'pending_payment' }],
        queryFn: () => api.get('/bookings', { query: { status: 'pending_payment', limit: 100 } }),
        refetchInterval: 60_000,
    });
    const awaitingPayment = (() => {
        const seen = new Set();
        return (awaitingPaymentQuery.data?.bookings || [])
            .filter((b) => {
                const key = b.booking_ref_master || b.ride_ref || b.id;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            })
            .sort((a, b) => String(a.pickup_date).localeCompare(String(b.pickup_date)));
    })();

        // Sent quotes still waiting for the client (not converted / expired / cancelled)
    const openQuotesQuery = useQuery({
        queryKey: ['quotes', { status: 'sent' }],
        queryFn: () => api.get('/quotes', { query: { status: 'sent', limit: 100 } }),
        refetchInterval: 5 * 60_000,
    });
    const openQuotes = (openQuotesQuery.data?.quotes || [])
        .filter((q) => !q.expires_at || new Date(q.expires_at) > new Date())
        .sort((a, b) => new Date(a.expires_at) - new Date(b.expires_at));

    // Past jobs never closed — any job dated yesterday or earlier that isn't
    // completed / cancelled / no-show. Today's jobs are never listed (they may
    // simply still be running). Awaiting-payment ones already have their own row.
    const yesterdayLocal = (() => {
        const d = new Date();
        d.setDate(d.getDate() - 1);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    })();
    const overdueQuery = useQuery({
        queryKey: ['bookings', 'overdue', yesterdayLocal],
        queryFn: () => api.get('/bookings', {
            query: {
                status: 'confirmed,allocated,dispatched,en_route,arrived,on_journey',
                date_from: '2025-01-01',
                date_to: yesterdayLocal,
                limit: 100,
            },
        }),
        refetchInterval: 5 * 60_000,
    });
    const overdue = overdueQuery.data?.bookings || [];

    const bookings = bookingsQuery.data?.bookings || [];
    const chauffeurs = chauffeursQuery.data?.drivers || [];
    const vehicles = vehiclesQuery.data?.vehicles || [];

    // Truly assignable = active AND compliant
    // is_compliant comes from the 7d.5.7 backend enrichment
    const assignableChauffeurs = chauffeurs.filter(c => c.is_compliant !== false);
    const assignableVehicles = vehicles.filter(v => v.is_compliant !== false);

    // Unassigned = booking has no allocated chauffeur yet (status = 'confirmed')
       const unassignedBookings = bookings.filter(b =>
        b.status === 'confirmed' || (b.status === 'pending_payment' && !b.allocated_chauffeur_id));

    // Attention items: chauffeurs/vehicles that ARE active but NOT compliant
    // (they show up as Inactive on detail pages, but the dashboard surfaces
    // them as a queue of things to fix)
    const chauffeursNeedingAttention = chauffeurs.filter(c => c.is_compliant === false);
    const vehiclesNeedingAttention = vehicles.filter(v => v.is_compliant === false);
const pendingUploadsCount = pendingUploadsQuery.data?.count || 0;
    const pendingInvoicesCount = pendingInvoicesQuery.data?.count || 0;
    const unpaidStatements = unpaidStatementsQuery.data?.statements || [];
    const unpaidStatementsCount = unpaidStatementsQuery.data?.count || 0;
 const unpaidStatementsTotalPence = unpaidStatementsQuery.data?.total_pence || 0;
    const pendingPoolChauffeurs = pendingPoolQuery.data?.chauffeurs || [];
    const pendingPoolCount = pendingPoolQuery.data?.count || 0;
       const attentionCount = chauffeursNeedingAttention.length + vehiclesNeedingAttention.length + pendingUploadsCount + pendingInvoicesCount + unpaidStatementsCount + pendingPoolCount + awaitingPayment.length + openQuotes.length + overdue.length;

    return (
        <div>
            <header className="mb-8 flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl font-semibold text-ink tracking-tight">
                        {greeting}, {firstName}
                    </h1>
                    <p className="text-sm text-ink-subtle mt-1">
                        Here's an overview of {dateLabelLower}'s operations.
                    </p>
                </div>
                <div className="inline-flex p-1 bg-canvas rounded-lg">
                    {[
                        { value: 'today', label: 'Today' },
                        { value: 'tomorrow', label: 'Tomorrow' },
                    ].map((opt) => {
                        const selected = dateView === opt.value;
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => setDateView(opt.value)}
                                className={`px-4 py-1.5 text-sm font-medium rounded transition-colors ${
                                    selected
                                        ? 'bg-paper text-ink shadow-sm'
                                        : 'text-ink-subtle hover:text-ink'
                                }`}
                            >
                                {opt.label}
                            </button>
                        );
                    })}
                </div>
            </header>

            {/* Stat tiles */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <DashboardTile
                    label={`Bookings ${dateLabelLower}`}
                    value={bookingsQuery.isLoading ? '—' : bookings.length}
                    hint={bookingsQuery.isLoading ? 'Loading' : `${bookings.filter(b => b.status === 'completed').length} completed`}
                    onClick={() => navigate(`/bookings?date_preset=${datePreset}`)}
                />
                <DashboardTile
                    label="Unassigned"
                    value={bookingsQuery.isLoading ? '—' : unassignedBookings.length}
                    hint={bookingsQuery.isLoading ? 'Loading' : (unassignedBookings.length === 0 ? 'All allocated' : 'Awaiting allocation')}
                                        onClick={() => navigate(`/bookings?date_preset=${datePreset}&status=confirmed,pending_payment`)}
                    tone={unassignedBookings.length > 0 ? 'attention' : null}
                />
                <DashboardTile
                    label="Active chauffeurs"
                    value={chauffeursQuery.isLoading ? '—' : assignableChauffeurs.length}
                    hint={chauffeursQuery.isLoading
                        ? 'Loading'
                        : (chauffeurs.length > assignableChauffeurs.length
                            ? `${chauffeurs.length - assignableChauffeurs.length} need attention`
                            : 'All available')
                    }
                    onClick={() => navigate('/chauffeurs')}
                />
                <DashboardTile
                    label="Active vehicles"
                    value={vehiclesQuery.isLoading ? '—' : assignableVehicles.length}
                    hint={vehiclesQuery.isLoading
                        ? 'Loading'
                        : (vehicles.length > assignableVehicles.length
                            ? `${vehicles.length - assignableVehicles.length} need attention`
                            : 'All available')
                    }
                    onClick={() => navigate('/vehicles')}
                />
            </div>

            {/* Selected day's bookings */}
            <div className="mt-8 card">
                <div className="card-header flex items-center justify-between gap-4">
                    <div>
                        <h2 className="text-sm font-semibold text-ink">{dateLabel}'s bookings</h2>
                        <p className="text-xs text-ink-subtle mt-0.5">
                            Live operational view. Refreshes every minute.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => navigate(`/bookings?date_preset=${datePreset}`)}
                        className="text-xs text-ink-muted hover:text-ink inline-flex items-center gap-1"
                    >
                        View all bookings
                        <ArrowRight className="h-3 w-3" />
                    </button>
                </div>

                {bookingsQuery.isLoading ? (
                    <div className="card-body">
                        <p className="text-sm text-ink-subtle text-center py-6">Loading…</p>
                    </div>
                ) : bookings.length === 0 ? (
                    <div className="card-body">
                        <div className="text-center py-8">
                            <Calendar className="h-8 w-8 text-ink-faint mx-auto mb-2" />
                            <p className="text-sm text-ink-subtle">No bookings {dateLabelLower}.</p>
                            <p className="text-xs text-ink-faint mt-1">Bookings will appear here as they come in.</p>
                        </div>
                    </div>
                ) : (
                    <ul className="divide-y divide-paper-border-soft">
                        {bookings.slice(0, 8).map(b => (
                            <BookingRow key={b.id} booking={b} onClick={() => navigate(`/bookings/${b.id}`)} />
                        ))}
                        {bookings.length > 8 && (
                            <li className="px-6 py-3 text-center">
                                <button
                                    type="button"
                                    onClick={() => navigate(`/bookings?date_preset=${datePreset}`)}
                                    className="text-xs text-ink-muted hover:text-ink"
                                >
                                    + {bookings.length - 8} more bookings {dateLabelLower}
                                </button>
                            </li>
                        )}
                    </ul>
                )}
            </div>

            {/* Attention items — only render if there are any */}
            {attentionCount > 0 && (
                <div className="mt-8 card border-amber-200">
                    <div className="card-header bg-amber-50 border-b border-amber-200">
                        <h2 className="text-sm font-semibold text-status-warning flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4" />
                            Needs attention
                        </h2>
                        <p className="text-xs text-status-warning/80 mt-0.5">
                            Active chauffeurs and vehicles needing attention.
                        </p>
                    </div>
                    <ul className="divide-y divide-paper-border-soft">
                                                {overdue.length > 0 && (
                            <OverdueJobsRow
                                bookings={overdue}
                                onSelect={(id) => navigate(`/bookings/${id}`)}
                            />
                        )}
                                                                        {openQuotes.length > 0 && (
                            <OpenQuotesRow
                                quotes={openQuotes}
                                onSelect={(id) => navigate(`/quotes/${id}`)}
                            />
                        )}

                        {awaitingPayment.length > 0 && (
                            <AwaitingPaymentRow
                                bookings={awaitingPayment}
                                onSelect={(id) => navigate(`/bookings/${id}`)}
                            />
                        )}

                        {pendingUploadsCount > 0 && (
                            <AttentionRow
                                key="pending-uploads"
                                title={`${pendingUploadsCount} ${pendingUploadsCount === 1 ? 'document' : 'documents'} awaiting review`}
                                subtitle="Portal uploads"
                                reasons={['pending_review']}
                                onClick={() => navigate('/compliance')}
                            />
                        )}

                        {pendingInvoicesCount > 0 && (
                            <AttentionRow
                                key="pending-invoices"
                                title={`${pendingInvoicesCount} ${pendingInvoicesCount === 1 ? 'booking' : 'bookings'} awaiting invoicing`}
                                subtitle="Bookings"
                                reasons={['pending_invoice']}
                                onClick={() => navigate('/bookings?invoice_status=not_invoiced&status=completed,no_show,cancelled&date_preset=all')}
                            />
                        )}

                        {pendingPoolCount > 0 && (
                            <PendingPoolRow
                                count={pendingPoolCount}
                                chauffeurs={pendingPoolChauffeurs}
                                onSelect={(id) => navigate(`/chauffeurs/${id}`)}
                            />
                        )}

                        {unpaidStatementsCount > 0 && (
                            <UnpaidStatementsRow
                                count={unpaidStatementsCount}
                                totalPence={unpaidStatementsTotalPence}
                                statements={unpaidStatements}
                                onSelect={(id) => navigate(`/statements/${id}`)}
                            />
                        )}
                        
                        {chauffeursNeedingAttention.length > 0 && (
                            <CompactAttentionGroup
                                title={`${chauffeursNeedingAttention.length} chauffeur${chauffeursNeedingAttention.length === 1 ? '' : 's'} need attention`}
                                subtitle="Documents & status"
                                items={chauffeursNeedingAttention.map(c => ({
                                    id: c.id,
                                    key: `c-${c.id}`,
                                    title: `${c.first_name} ${c.last_name}`,
                                    reasons: c.inactive_reasons || [],
                                }))}
                                onSelect={(id) => navigate(`/chauffeurs/${id}`)}
                            />
                        )}
                        {vehiclesNeedingAttention.length > 0 && (
                            <CompactAttentionGroup
                                title={`${vehiclesNeedingAttention.length} vehicle${vehiclesNeedingAttention.length === 1 ? '' : 's'} need attention`}
                                subtitle="Documents & status"
                                items={vehiclesNeedingAttention.map(v => ({
                                    id: v.id,
                                    key: `v-${v.id}`,
                                    title: `${v.registration} — ${v.make} ${v.model}`,
                                    reasons: v.inactive_reasons || [],
                                }))}
                                onSelect={(id) => navigate(`/vehicles/${id}`)}
                            />
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}

// =============================================================================
// DashboardTile — clickable stat card
// =============================================================================
function DashboardTile({ label, value, hint, onClick, tone }) {
    const isAttention = tone === 'attention';

    return (
        <button
            type="button"
            onClick={onClick}
            className={
                'card card-body text-left transition-colors hover:bg-paper-hover/30 ' +
                (isAttention ? 'border-amber-200' : '')
            }
        >
            <div className="text-xs font-medium uppercase tracking-wider text-ink-subtle">{label}</div>
            <div className={
                'mt-2 text-3xl font-semibold tabular-nums tracking-tight ' +
                (isAttention ? 'text-status-warning' : 'text-ink')
            }>
                {value}
            </div>
            {hint && (
                <div className={
                    'mt-1 text-xs ' +
                    (isAttention ? 'text-status-warning/80' : 'text-ink-faint')
                }>
                    {hint}
                </div>
            )}
        </button>
    );
}

// =============================================================================
// BookingRow — single booking in the today's feed
// =============================================================================
function BookingRow({ booking: b, onClick }) {
    // pickup_time is a plain "HH:MM" string in the DB, not a datetime —
    // display it directly. Falls back to em-dash for hourly bookings without
    // a fixed time (rare, but the schema allows it).
    const pickupTime = b.pickup_time || '—';

    // Passenger name is in pax_name field (not passenger_name).
    // Falls back to booker name if no separate passenger was set.
    const displayName = b.pax_name || b.booker_name || 'Unknown';

    return (
        <li
            onClick={onClick}
            className="px-6 py-3 flex items-center gap-4 cursor-pointer hover:bg-paper-hover/50 transition-colors"
        >
            <div className="text-sm font-mono font-medium text-ink w-12 flex-shrink-0">
                {pickupTime}
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-ink truncate">{displayName}</div>
                <div className="text-xs text-ink-subtle truncate">
                    {b.pickup_address || '—'}
                    {b.dropoff_address && ` → ${b.dropoff_address}`}
                </div>
            </div>
            <div className="flex-shrink-0">
                <BookingStatusPill status={b.status} />
            </div>
        </li>
    );
}

// =============================================================================
// AttentionRow — single attention item
// =============================================================================
function AttentionRow({ title, subtitle, reasons, onClick }) {
   const reasonLabels = {
        manually_disabled: 'Disabled by owner',
        documents_expired: 'Documents expired',
        documents_missing: 'Documents missing',
        engagement_ended: 'Engagement ended',
        pending_review: 'Awaiting your review',
       pending_invoice: 'Pending invoices',
    };
    const reasonText = reasons.map(r => reasonLabels[r] || r).join(' · ');

    return (
        <li
            onClick={onClick}
            className="px-6 py-3 flex items-center justify-between gap-4 cursor-pointer hover:bg-paper-hover/50 transition-colors"
        >
            <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-ink truncate">{title}</div>
                <div className="text-xs text-ink-subtle mt-0.5">
                    {subtitle} · {reasonText}
                </div>
            </div>
            <ArrowRight className="h-4 w-4 text-ink-faint flex-shrink-0" />
        </li>
    );
}

// =============================================================================
// Status pill mapping for booking statuses
// =============================================================================
function BookingStatusPill({ status }) {
    const config = {
                pending_payment: { tone: 'warning', label: 'Pending payment' },
        confirmed: { tone: 'neutral', label: 'Confirmed' },
        allocated: { tone: 'neutral', label: 'Allocated' },
        dispatched: { tone: 'info', label: 'Dispatched' },
        en_route: { tone: 'warning', label: 'En route' },
        arrived: { tone: 'warning', label: 'Arrived' },
        on_journey: { tone: 'warning', label: 'On journey' },
        completed: { tone: 'success', label: 'Completed' },
        pending_review: { tone: 'warning', label: 'Pending review' },
        cancelled: { tone: 'danger', label: 'Cancelled' },
        no_show: { tone: 'danger', label: 'No-show' },
    };
    const { tone, label } = config[status] || { tone: 'neutral', label: status };
    return <StatusPill status={tone}>{label}</StatusPill>;
}
// =============================================================================
// UnpaidStatementsRow — collapsible list of all unpaid statements.
// Collapsed shows total count + amount; expanded lists each statement with
// chauffeur, period, amount, and age. Each row click → /statements/:id.
// =============================================================================
function UnpaidStatementsRow({ count, totalPence, statements, onSelect }) {
    const [expanded, setExpanded] = useState(false);
    const totalLabel = `£${(totalPence / 100).toFixed(2)}`;

    return (
        <li>
            <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="w-full px-6 py-3 flex items-center justify-between gap-4 cursor-pointer hover:bg-paper-hover/50 transition-colors text-left"
            >
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-ink truncate">
                        {count} unpaid statement{count === 1 ? '' : 's'} · {totalLabel} owed
                    </div>
                    <div className="text-xs text-ink-subtle mt-0.5">
                        Chauffeur pay · click to {expanded ? 'collapse' : 'expand'}
                    </div>
                </div>
                {expanded
                    ? <ChevronDown className="h-4 w-4 text-ink-faint flex-shrink-0" />
                    : <ChevronRightIcon className="h-4 w-4 text-ink-faint flex-shrink-0" />}
            </button>

            {expanded && (
                <ul className="bg-canvas/40 border-t border-paper-border-soft divide-y divide-paper-border-soft">
                    {statements.map((s) => {
                        const age = daysSince(s.issued_at);
                        const amount = `£${((s.grand_total_pence || 0) / 100).toFixed(2)}`;
                        return (
                            <li
                                key={s.id}
                                onClick={() => onSelect(s.id)}
                                className="px-6 py-2.5 pl-12 flex items-center justify-between gap-4 cursor-pointer hover:bg-paper-hover/50 transition-colors"
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium text-ink truncate">
                                        #{s.statement_number} · {s.chauffeur_first_name} {s.chauffeur_last_name}
                                    </div>
                                    <div className="text-xs text-ink-subtle mt-0.5">
                                        {formatPeriod(s.period_start, s.period_end)} · issued {age} day{age === 1 ? '' : 's'} ago
                                        {!s.emailed_at && ' · not emailed'}
                                    </div>
                                </div>
                                <div className="text-sm font-medium text-ink flex-shrink-0">
                                    {amount}
                                </div>
                                <ArrowRight className="h-4 w-4 text-ink-faint flex-shrink-0" />
                            </li>
                        );
                    })}
                </ul>
            )}
        </li>
    );
}

// =============================================================================
// CompactAttentionGroup — collapsible wrapper for a list of attention items
// (chauffeurs with documents/status to update). Collapsed shows the count;
// expanded lists each entry with its reasons. Mirrors PendingPoolRow /
// UnpaidStatementsRow so the "Needs attention" card stays scannable instead
// of one full-height row per chauffeur.
// =============================================================================
const ATTENTION_REASON_LABELS = {
    manually_disabled: 'Disabled by owner',
    documents_expired: 'Documents expired',
    documents_missing: 'Documents missing',
    engagement_ended: 'Engagement ended',
    pending_review: 'Awaiting your review',
    pending_invoice: 'Pending invoices',
};

function CompactAttentionGroup({ title, subtitle, items, onSelect }) {
    const [expanded, setExpanded] = useState(false);

    return (
        <li>
            <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="w-full px-6 py-3 flex items-center justify-between gap-4 cursor-pointer hover:bg-paper-hover/50 transition-colors text-left"
            >
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-ink truncate">{title}</div>
                    <div className="text-xs text-ink-subtle mt-0.5">
                        {subtitle} · click to {expanded ? 'collapse' : 'expand'}
                    </div>
                </div>
                {expanded
                    ? <ChevronDown className="h-4 w-4 text-ink-faint flex-shrink-0" />
                    : <ChevronRightIcon className="h-4 w-4 text-ink-faint flex-shrink-0" />}
            </button>

            {expanded && (
                <ul className="bg-canvas/40 border-t border-paper-border-soft divide-y divide-paper-border-soft">
                    {items.map((it) => {
                        const reasonText = (it.reasons || [])
                            .map((r) => ATTENTION_REASON_LABELS[r] || r)
                            .join(' · ');
                        return (
                            <li
                                key={it.key}
                                onClick={() => onSelect(it.id)}
                                className="px-6 py-2.5 pl-12 flex items-center justify-between gap-4 cursor-pointer hover:bg-paper-hover/50 transition-colors"
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium text-ink truncate">{it.title}</div>
                                    {reasonText && (
                                        <div className="text-xs text-ink-subtle mt-0.5">{reasonText}</div>
                                    )}
                                </div>
                                <ArrowRight className="h-4 w-4 text-ink-faint flex-shrink-0" />
                            </li>
                        );
                    })}
                </ul>
            )}
        </li>
    );
}

function PendingPoolRow({ count, chauffeurs, onSelect }) {
    const [expanded, setExpanded] = useState(false);
    const totalPence = chauffeurs.reduce((s, c) => s + (c.net_total_pence || 0), 0);
    const totalLabel = `£${(totalPence / 100).toFixed(2)}`;

    return (
        <li>
            <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="w-full px-6 py-3 flex items-center justify-between gap-4 cursor-pointer hover:bg-paper-hover/50 transition-colors text-left"
            >
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-ink truncate">
                        {count} chauffeur{count === 1 ? '' : 's'} ready to statement · {totalLabel} net
                    </div>
                    <div className="text-xs text-ink-subtle mt-0.5">
                        Chauffeur pay · click to {expanded ? 'collapse' : 'expand'}
                    </div>
                </div>
                {expanded
                    ? <ChevronDown className="h-4 w-4 text-ink-faint flex-shrink-0" />
                    : <ChevronRightIcon className="h-4 w-4 text-ink-faint flex-shrink-0" />}
            </button>

            {expanded && (
                <ul className="bg-canvas/40 border-t border-paper-border-soft divide-y divide-paper-border-soft">
                    {chauffeurs.map((c) => (
                        <li
                            key={c.chauffeur_id}
                            onClick={() => onSelect(c.chauffeur_id)}
                            className="px-6 py-2.5 pl-12 flex items-center justify-between gap-4 cursor-pointer hover:bg-paper-hover/50 transition-colors"
                        >
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-ink truncate">
                                    {c.chauffeur_first_name} {c.chauffeur_last_name}
                                </div>
                                <div className="text-xs text-ink-subtle mt-0.5">
                                    {c.line_count} line{c.line_count === 1 ? '' : 's'} in pool
                                </div>
                            </div>
                            <div className="text-sm font-medium text-ink flex-shrink-0">
                                £{((c.net_total_pence || 0) / 100).toFixed(2)}
                            </div>
                            <ArrowRight className="h-4 w-4 text-ink-faint flex-shrink-0" />
                        </li>
                    ))}
                </ul>
            )}
        </li>
    );
}

function daysSince(iso) {
    if (!iso) return 0;
    const then = new Date(iso);
    const now = new Date();
    return Math.floor((now - then) / (1000 * 60 * 60 * 24));
}

function quoteTotal(q) {
    if (q.master_total != null) return Number(q.master_total);
    const pricings = Array.isArray(q.pricings) ? q.pricings : [];
    return pricings.reduce((s, p) => s + (Number(p?.total) || 0), 0);
}

function OpenQuotesRow({ quotes, onSelect }) {
    const [expanded, setExpanded] = useState(false);
    const count = quotes.length;
    const total = quotes.reduce((s, q) => s + quoteTotal(q), 0);

    return (
        <li>
            <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="w-full px-6 py-3 flex items-center justify-between gap-4 cursor-pointer hover:bg-paper-hover/50 transition-colors text-left"
            >
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-ink truncate">
                        {count} quote{count === 1 ? '' : 's'} awaiting reply · £{total.toFixed(2)}
                    </div>
                    <div className="text-xs text-ink-subtle mt-0.5">
                        Sent, not yet booked · click to {expanded ? 'collapse' : 'expand'}
                    </div>
                </div>
                {expanded
                    ? <ChevronDown className="h-4 w-4 text-ink-faint flex-shrink-0" />
                    : <ChevronRightIcon className="h-4 w-4 text-ink-faint flex-shrink-0" />}
            </button>

            {expanded && (
                <ul className="bg-canvas/40 border-t border-paper-border-soft divide-y divide-paper-border-soft">
                    {quotes.map((q) => {
                        const sentAgo = daysSince(q.sent_at);
                        const expires = q.expires_at
                            ? new Date(q.expires_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
                            : '—';
                        const who = q.customer_name || q.linked_customer_name || q.customer_email || 'Client';
                        return (
                            <li
                                key={q.id}
                                onClick={() => onSelect(q.id)}
                                className="px-6 py-2.5 pl-12 flex items-center justify-between gap-4 cursor-pointer hover:bg-paper-hover/50 transition-colors"
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium text-ink truncate">
                                        {q.quote_ref} · {who}
                                    </div>
                                    <div className="text-xs text-ink-subtle mt-0.5">
                                        Sent {sentAgo === 0 ? 'today' : `${sentAgo} day${sentAgo === 1 ? '' : 's'} ago`} · expires {expires}
                                    </div>
                                </div>
                                <div className="text-sm font-medium text-ink flex-shrink-0">£{quoteTotal(q).toFixed(2)}</div>
                                <ArrowRight className="h-4 w-4 text-ink-faint flex-shrink-0" />
                            </li>
                        );
                    })}
                </ul>
            )}
        </li>
    );
}

function AwaitingPaymentRow({ bookings, onSelect }) {
    const [expanded, setExpanded] = useState(false);
    const count = bookings.length;
    const total = bookings.reduce((s, b) => s + Number(b.master_charged_total ?? b.quoted_price ?? 0), 0);

    return (
        <li>
            <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="w-full px-6 py-3 flex items-center justify-between gap-4 cursor-pointer hover:bg-paper-hover/50 transition-colors text-left"
            >
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-ink truncate">
                        {count} booking{count === 1 ? '' : 's'} awaiting payment · £{total.toFixed(2)}
                    </div>
                    <div className="text-xs text-ink-subtle mt-0.5">
                        Payments · click to {expanded ? 'collapse' : 'expand'}
                    </div>
                </div>
                {expanded
                    ? <ChevronDown className="h-4 w-4 text-ink-faint flex-shrink-0" />
                    : <ChevronRightIcon className="h-4 w-4 text-ink-faint flex-shrink-0" />}
            </button>

            {expanded && (
                <ul className="bg-canvas/40 border-t border-paper-border-soft divide-y divide-paper-border-soft">
                    {bookings.map((b) => {
                        const amount = Number(b.master_charged_total ?? b.quoted_price ?? 0);
                        const date = b.pickup_date
                            ? new Date(String(b.pickup_date).slice(0, 10) + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                            : '';
                        return (
                            <li
                                key={b.id}
                                onClick={() => onSelect(b.id)}
                                className="px-6 py-2.5 pl-12 flex items-center justify-between gap-4 cursor-pointer hover:bg-paper-hover/50 transition-colors"
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium text-ink truncate">
                                        {b.booking_ref_master || b.ride_ref} · {b.customer_name || b.booker_name || 'Customer'}
                                    </div>
                                    <div className="text-xs text-ink-subtle mt-0.5">
                                        Pickup {date}{b.pickup_time ? ` ${String(b.pickup_time).slice(0, 5)}` : ''}
                                        {b.allocated_chauffeur_id ? ' · chauffeur allocated' : ''}
                                    </div>
                                </div>
                                <div className="text-sm font-medium text-ink flex-shrink-0">£{amount.toFixed(2)}</div>
                                <ArrowRight className="h-4 w-4 text-ink-faint flex-shrink-0" />
                            </li>
                        );
                    })}
                </ul>
            )}
        </li>
    );
}

// =============================================================================
// OverdueJobsRow — past jobs still open (not completed / cancelled / no-show)
// =============================================================================
function OverdueJobsRow({ bookings, onSelect }) {
    const [expanded, setExpanded] = useState(false);
    const count = bookings.length;

    return (
        <li>
            <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="w-full px-6 py-3 flex items-center justify-between gap-4 cursor-pointer hover:bg-paper-hover/50 transition-colors text-left"
            >
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-ink truncate">
                        {count} job{count === 1 ? '' : 's'} not completed
                    </div>
                    <div className="text-xs text-ink-subtle mt-0.5">
                        Past pickup, still open · click to {expanded ? 'collapse' : 'expand'}
                    </div>
                </div>
                {expanded
                    ? <ChevronDown className="h-4 w-4 text-ink-faint flex-shrink-0" />
                    : <ChevronRightIcon className="h-4 w-4 text-ink-faint flex-shrink-0" />}
            </button>

            {expanded && (
                <ul className="bg-canvas/40 border-t border-paper-border-soft divide-y divide-paper-border-soft">
                    {bookings.map((b) => {
                        const date = b.pickup_date
                            ? new Date(String(b.pickup_date).slice(0, 10) + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                            : '';
                                                const chauffeur = (b.chauffeur_name || '').trim();
                        return (
                            <li
                                key={b.id}
                                onClick={() => onSelect(b.id)}
                                className="px-6 py-2.5 pl-12 flex items-center justify-between gap-4 cursor-pointer hover:bg-paper-hover/50 transition-colors"
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium text-ink truncate">
                                        {b.ride_ref} · {b.customer_name || b.booker_name || 'Customer'}
                                    </div>
                                    <div className="text-xs text-ink-subtle mt-0.5">
                                        Pickup {date}{b.pickup_time ? ` ${String(b.pickup_time).slice(0, 5)}` : ''}
                                        {chauffeur ? ` · ${chauffeur}` : ' · no chauffeur'}
                                    </div>
                                </div>
                                <div className="flex-shrink-0">
                                    <BookingStatusPill status={b.status} />
                                </div>
                                <ArrowRight className="h-4 w-4 text-ink-faint flex-shrink-0" />
                            </li>
                        );
                    })}
                </ul>
            )}
        </li>
    );
}

function formatPeriod(start, end) {
    const s = new Date(start).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    const e = new Date(end).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    return `${s} – ${e}`;
}
